import { useState, type FormEvent } from 'react';
import type { SupportAccessGrant } from '@traceio-org/platform-client';
import { supportAccessRequestSchema } from '@traceio-org/platform-contracts';
import { EmptyPageState, NavigationGlyph, PageSection } from '@traceio-org/ui';
import { describeWriteFailure, type WriteFailure } from '../../api';
import { AdminProblem } from '../../components/AdminProblem';
import { LoadingBlock, SavedNotice, useNow } from '../../components/feedback';
import {
  RadioGroup,
  ReasonField,
  SelectField,
  TextAreaField,
  TextField,
  reasonError,
} from '../../components/fields';
import { GrantsTable, SCOPE_LABELS } from '../../components/grantsTable';
import { WriteDialog, WriteFailureMessage, useDialogTarget } from '../../components/WriteDialog';
import { useAdmin } from '../../context';
import { formatDateTime, formatDuration } from '../../format';
import { isGrantActive, useOrganizationScope } from './scope';

/** The durations a grant may last (supportAccessRequestSchema: 15 to 480 minutes). */
export const GRANT_DURATIONS = [15, 30, 60, 120, 240, 480] as const;
const DEFAULT_DURATION = 60;

const REASON_MIN = 10;
const REASON_MAX = 1000;
const TICKET_MAX = 100;

const SCOPE_CHOICES = [
  {
    value: 'read',
    label: SCOPE_LABELS.read,
    description: 'See the organization’s members, invitations, configuration and audit log.',
  },
  {
    value: 'write',
    label: SCOPE_LABELS.write,
    description: 'Also make the changes your staff role allows that need support access.',
  },
] as const;

interface RequestForm {
  scope: 'read' | 'write';
  duration: string;
  reason: string;
  ticket: string;
}

type RequestErrors = Partial<Record<keyof RequestForm, string>>;

function validateRequest(form: RequestForm): RequestErrors {
  const errors: RequestErrors = {};
  const reason = form.reason.trim().length;
  if (reason === 0) errors.reason = 'Enter the reason you need access.';
  else if (reason < REASON_MIN)
    errors.reason = `Write at least ${REASON_MIN} characters, so the reason says what you will look at.`;
  else if (reason > REASON_MAX) errors.reason = `Write at most ${REASON_MAX} characters.`;
  if (form.ticket.trim().length > TICKET_MAX)
    errors.ticket = `A ticket reference has at most ${TICKET_MAX} characters.`;
  return errors;
}

/**
 * Support access (/organizations/:id/support-access): staff who are not platform admins read an organization's
 * data beyond its metadata only during a grant. Here they request one (scope, reason, ticket, duration), see
 * that it is on (the banner above every section says so while it lasts) and the grants recorded for this
 * organization. Platform admins may request one too, to record why they look.
 */
export function SupportAccessTab() {
  const { organization, abilities, grants, activeGrant, staffUserId } = useOrganizationScope();
  const { api } = useAdmin();
  // Re-rendered by the tick; each render reads the clock afresh (a grant that just started is in force).
  const now = Math.max(useNow(15_000), Date.now());
  const [form, setForm] = useState<RequestForm>({
    scope: 'read',
    duration: String(DEFAULT_DURATION),
    reason: '',
    ticket: '',
  });
  const [errors, setErrors] = useState<RequestErrors>({});
  const [failure, setFailure] = useState<WriteFailure | null>(null);
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const name = organization.displayName;
  // Ending a grant from the list: its holder, or a platform admin for anyone's (platform-api decides again).
  const ending = useDialogTarget<SupportAccessGrant>();
  const [endReason, setEndReason] = useState('');
  const [endProblem, setEndProblem] = useState<string | null>(null);
  const canEnd = (grant: SupportAccessGrant) =>
    grant.staffUserId === staffUserId || abilities.readsWithoutGrant;
  const list =
    grants.data && !grants.failure
      ? grants.data.items.filter((g) => g.organizationId === organization.id)
      : null;

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (sending) return;
    const found = validateRequest(form);
    setErrors(found);
    setFailure(null);
    if (Object.keys(found).length > 0) return;
    const request = {
      organizationId: organization.id,
      scope: form.scope,
      reason: form.reason.trim(),
      durationMinutes: Number(form.duration),
      ...(form.ticket.trim() ? { ticketRef: form.ticket.trim() } : {}),
    };
    // The request goes through the contract's own schema first, so the form can never send what it refuses.
    if (!supportAccessRequestSchema.safeParse(request).success) {
      setErrors({ reason: 'Check the reason, the ticket and the duration.' });
      return;
    }
    setSending(true);
    try {
      const grant = await api.requestSupportAccess(request);
      setNotice(`Support access to ${name} is on until ${formatDateTime(grant.expiresAt)}.`);
      setForm({ scope: 'read', duration: String(DEFAULT_DURATION), reason: '', ticket: '' });
      grants.retry();
    } catch (error) {
      const described = describeWriteFailure(error);
      if (described.kind === 'saved-unreadable') {
        console.warn('admin-web: support access was granted but its answer is outside the contract', error);
        setNotice(`Support access to ${name} is on.`);
        grants.retry();
      } else {
        setFailure(described);
      }
    } finally {
      setSending(false);
    }
  };

  let yours;
  if (!grants.data && !grants.failure) {
    // Until the grants are known, a grant in force could be among them: no form that could ask for a second one.
    yours = <LoadingBlock label="Checking your support access" />;
  } else if (activeGrant) {
    yours = (
      <p>
        Your {SCOPE_LABELS[activeGrant.scope].toLowerCase()} access to {name} lasts until{' '}
        <strong>{formatDateTime(activeGrant.expiresAt)}</strong>. To end it early, use{' '}
        <em>End support access now</em> in the banner above.
      </p>
    );
  } else if (!abilities.requestSupportAccess) {
    yours = (
      <p className="admin-muted">
        Your staff role cannot request support access. Ask a support operator or a platform admin when someone
        needs to look at {name}&apos;s data.
      </p>
    );
  } else {
    yours = (
      <form
        className="admin-form"
        onSubmit={(event) => void submit(event)}
        noValidate
        aria-label="Request support access"
      >
        {abilities.readsWithoutGrant ? (
          <p className="admin-muted">
            As a platform admin you read {name}&apos;s data without support access; every read is still
            recorded. A grant records why you look.
          </p>
        ) : null}
        {failure ? <WriteFailureMessage failure={failure} /> : null}
        <RadioGroup
          legend="Scope"
          choices={SCOPE_CHOICES}
          value={form.scope}
          onChange={(value) => setForm((current) => ({ ...current, scope: value as RequestForm['scope'] }))}
        />
        <SelectField
          label="Duration"
          value={form.duration}
          options={GRANT_DURATIONS.map((minutes) => ({
            value: String(minutes),
            label: formatDuration(minutes),
          }))}
          onChange={(value) => setForm((current) => ({ ...current, duration: value }))}
          hint="Access ends by itself at the end of it."
        />
        <TextAreaField
          label="Reason"
          value={form.reason}
          onChange={(value) => setForm((current) => ({ ...current, reason: value }))}
          error={errors.reason ?? null}
          maxLength={REASON_MAX}
          required
          hint={`What you need to look at and why: ${REASON_MIN} to ${REASON_MAX} characters, recorded with every read.`}
        />
        <TextField
          label="Ticket reference (optional)"
          value={form.ticket}
          onChange={(value) => setForm((current) => ({ ...current, ticket: value }))}
          error={errors.ticket ?? null}
          maxLength={TICKET_MAX}
          spellCheck={false}
          hint="The support ticket this access is for, for example SUP-4412."
        />
        <div className="admin-form__actions">
          <button
            type="submit"
            className="admin-action admin-action--primary"
            aria-disabled={sending || undefined}
          >
            Request support access
          </button>
        </div>
      </form>
    );
  }

  return (
    <>
      <SavedNotice message={notice} />
      <PageSection
        title="Your support access"
        description={`Reading ${name}'s data beyond its name and settings needs a time-limited grant, except for platform admins. Every read is recorded.`}
      >
        {yours}
      </PageSection>
      <PageSection title="Support access grants" description={`Grants recorded for ${name}, newest first.`}>
        {grants.failure ? (
          <AdminProblem failure={grants.failure} subject="the support access grants" onRetry={grants.retry} />
        ) : !list ? (
          <LoadingBlock label="Loading the support access grants" />
        ) : list.length === 0 ? (
          <EmptyPageState
            icon={<NavigationGlyph name="Headphones" />}
            title="No support access grants"
            description={`Nobody you can see has had support access to ${name}.`}
          />
        ) : (
          <GrantsTable
            grants={list}
            caption="Support access grants"
            staffUserId={staffUserId}
            now={now}
            busy={grants.loading}
            {...((list ?? []).some((grant) => isGrantActive(grant, now) && canEnd(grant))
              ? {
                  onEnd: {
                    canEnd,
                    end: (grant: SupportAccessGrant) => {
                      setEndReason('');
                      setEndProblem(null);
                      ending.show(grant);
                    },
                  },
                }
              : {})}
          />
        )}
      </PageSection>
      {ending.target ? (
        <WriteDialog
          key={ending.key}
          open={ending.open}
          onClose={ending.close}
          title="End this support access now?"
          description="Its holder stops reading the organization's data at once."
          submitLabel="End support access"
          onSubmit={async () => {
            const problem = endReason.trim() ? reasonError(endReason) : null;
            setEndProblem(problem);
            if (problem) return 'invalid';
            await api.revokeSupportAccess(ending.target!.id, endReason);
            return undefined;
          }}
          onSaved={() => {
            setNotice('The support access has ended.');
            grants.retry();
          }}
        >
          <ReasonField optional value={endReason} onChange={setEndReason} error={endProblem} />
        </WriteDialog>
      ) : null}
    </>
  );
}
